import React, { useState, useEffect } from 'react';
import { Map, Layers, Filter, MapPin, Truck, BarChart3, Settings, Loader } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useUser } from '../contexts/UserContext';
import { useActivityLogger } from '../hooks/useActivityLogger';
import MapResults from './MapResults';

function MapView() {
  const { freightData, loading, error, dataSource, getDataStats } = useData();
  const { userRole } = useUser();
  const { logHeatMap } = useActivityLogger();
  const [filteredData, setFilteredData] = useState([]);
  const [mapFilters, setMapFilters] = useState({
    dataType: 'loads',
    equipment: 'all',
    dateRange: 'all',
    viewType: 'state'
  });
  
  // Separate state for display-only settings (don't trigger API calls)
  const [displaySettings, setDisplaySettings] = useState({
    colorStyle: 'orange-red',
    colorMetric: 'load_count',  // Color By metric for Heat Map
    colorScale: 'linear'  // Color scaling method
  });
  // Separate state for backend-affecting filters only
  const [backendFilters, setBackendFilters] = useState({
    equipment: 'all',
    dateRange: 'all',
    viewType: 'state'
  });
  const [mapStats, setMapStats] = useState({
    activeLocations: 0,
    activeLanes: 0,
    avgRatePerMile: 0
  });
  const [showMap, setShowMap] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);

  // Helper function to get equipment types for backend filtering
  const getEquipmentTypes = (equipmentFilter) => {
    const equipmentMap = {
      'flatbed': ['F', 'FA', 'FSD'],
      'dry-van': ['V'],
      'reefer': ['R'],
      'hot-shot': ['HS']
    };
    return equipmentMap[equipmentFilter] || [];
  };

  // Helper function to calculate date range based on preset (exact same logic as Rate Insights)
  const calculateDateRange = (preset) => {
    const today = new Date();
    const end = new Date(today);
    const start = new Date(today);
    
    if (preset === 'next-7-days') {
      // For next 7 days: start = today, end = today + 7
      end.setDate(today.getDate() + 7);
    } else {
      // For past periods: start = today + negative days, end = today
      const days = { 'last-3-weeks': -21, 'last-2-weeks': -14, 'last-week': -7 };
      start.setDate(today.getDate() + (days[preset] || -7));
    }
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  // Helper function to get date range for backend filtering
  const getDateRangeFilter = (dateRange) => {
    if (dateRange === 'all') return null;
    
    return calculateDateRange(dateRange);
  };

  // Check if we should enable the map button - only disable when actively loading
  const isMapButtonDisabled = () => {
    return mapLoading; // Only disable when map is actively loading
  };

  // Filter data based on backend filters (not visual filters)
  useEffect(() => {
    console.log(`[MapView] Filtering data - freightData: ${freightData?.length}, loading: ${loading}, dataSource: ${dataSource}`);
    
    if (!freightData || freightData.length === 0) {
      // If no data but not loading, set empty filtered data
      if (!loading) {
        console.log(`[MapView] Setting empty filtered data - no freight data available`);
        setFilteredData([]);
      }
      return;
    }

    console.log(`[MapView] Starting with ${freightData.length} freight records`);
    let filtered = [...freightData];

    // Equipment filter
    if (backendFilters.equipment !== 'all') {
      const equipmentMap = {
        'flatbed': ['F', 'FA', 'FSD'],
        'dry-van': ['V'],
        'reefer': ['R'],
        'hot-shot': ['HS']
      };
      const equipmentTypes = equipmentMap[backendFilters.equipment] || [];
      filtered = filtered.filter(load => equipmentTypes.includes(load.Equipment));
    }

    // Date range filter
    if (backendFilters.dateRange !== 'all') {
      const dateRange = getDateRangeFilter(backendFilters.dateRange);
      console.log(`[MapView] Date range filter: ${backendFilters.dateRange}`, dateRange);
      
      if (dateRange) {
        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);
        const beforeFilter = filtered.length;
        
        filtered = filtered.filter(load => {
          const loadDate = new Date(load.PickupDate);
          return loadDate >= startDate && loadDate <= endDate;
        });
        
        console.log(`[MapView] Date filtering: ${beforeFilter} -> ${filtered.length} records (${dateRange.start} to ${dateRange.end})`);
      }
    }

    console.log(`[MapView] Final filtered data: ${filtered.length} records`);
    setFilteredData(filtered);

    // Calculate statistics
    const stats = {
      activeLocations: new Set([
        ...filtered.map(load => `${load.OriginCity}, ${load.OriginState}`),
        ...filtered.map(load => `${load.DestinationCity}, ${load.DestinationState}`)
      ]).size,
      activeLanes: new Set(
        filtered.map(load => `${load.OriginState}-${load.DestinationState}`)
      ).size,
      avgRatePerMile: filtered.length > 0 
        ? filtered.reduce((sum, load) => sum + (load.RatePerMile || 0), 0) / filtered.length 
        : 0
    };
    setMapStats(stats);
  }, [freightData, backendFilters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setMapFilters(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Also update backend filters for backend-affecting controls
    if (['equipment', 'dateRange', 'viewType'].includes(name)) {
      setBackendFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handler for visual-only changes that don't trigger backend re-fetch
  const handleVisualChange = (e) => {
    const { name, value } = e.target;
    setMapFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // This only updates the state without triggering useEffect that depends on filters
  };

  const handleLoadMap = async () => {
    setMapLoading(true);

    // If we don't have data yet, the map will handle it
    console.log(`[MapView] Load Map clicked - Current data: ${filteredData?.length || 0} records`);

    // Log heat map activity using centralized logger
    try {
      await logHeatMap({
        equipment_filter: mapFilters.equipment,
        date_range: mapFilters.dateRange,
        view_type: mapFilters.viewType
      });
    } catch (logError) {
      console.warn('Failed to log heat map activity:', logError);
    }

    // Small delay for UX feedback
    await new Promise(resolve => setTimeout(resolve, 500));
    setShowMap(true);
    setMapLoading(false);
  };

  const handleRefreshMap = async () => {
    setMapLoading(true);
    setShowMap(false);

    // Log heat map activity using centralized logger
    try {
      await logHeatMap({
        equipment_filter: mapFilters.equipment,
        date_range: mapFilters.dateRange,
        view_type: mapFilters.viewType
      });
    } catch (logError) {
      console.warn('Failed to log heat map activity:', logError);
    }

    // Simulate refresh delay
    await new Promise(resolve => setTimeout(resolve, 500));
    setShowMap(true);
    setMapLoading(false);
  };

  // Handler for display settings (no API calls)
  const handleDisplaySettingsChange = (e) => {
    const { name, value } = e.target;
    setDisplaySettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Interactive Map</h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Visualize freight data, lanes, and market insights
        </p>
        <div className="mt-4 flex justify-center">
        </div>
      </div>

      {/* Basic Plan Notice */}
      {userRole === 'Basic' && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <div className="w-5 h-5 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 text-xs">ℹ</span>
            </div>
            <div>
              <p className="text-blue-700 dark:text-blue-300 text-sm font-medium">Basic Plan - Limited Usage</p>
              <p className="text-blue-600 dark:text-blue-400 text-sm">
                Map features are rate limited for Basic users. Upgrade to a paid plan for unlimited access.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Map Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-4">
          <Layers className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Map Controls</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="equipment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Equipment Filter
            </label>
            <select
              id="equipment"
              name="equipment"
              value={mapFilters.equipment}
              onChange={handleFilterChange}
              className="form-select dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All Equipment</option>
              <option value="flatbed">Flatbed</option>
              <option value="dry-van">Dry Van</option>
              <option value="reefer">Reefer</option>
              <option value="hot-shot">Hot Shot</option>
            </select>
          </div>

          <div>
            <label htmlFor="dateRange" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date Range
            </label>
            <select
              id="dateRange"
              name="dateRange"
              value={mapFilters.dateRange}
              onChange={handleFilterChange}
              className="form-select dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="all">All Time</option>
              <option value="last-3-weeks">Last 3 Weeks</option>
              <option value="last-2-weeks">Last 2 Weeks</option>
              <option value="last-week">Last Week</option>
              <option value="next-7-days">Next 7 Days</option>
            </select>
          </div>


          {/* View Type */}
          <div>
            <label htmlFor="viewType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              View Type
            </label>
            <select
              id="viewType"
              name="viewType"
              value={mapFilters.viewType}
              onChange={handleFilterChange}
              className="form-select dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="city">City (Lat/Lon)</option>
              <option value="county">County (FIPS)</option>
              <option value="state">State</option>
              <option value="region">Region</option>
            </select>
          </div>

          {/* Load/Refresh Map Button */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Map Action
            </label>
            {!showMap ? (
              <button
                onClick={handleLoadMap}
                disabled={isMapButtonDisabled()}
                className="w-full inline-flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {mapLoading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <Map className="w-4 h-4" />
                    <span>Load Map</span>
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleRefreshMap}
                disabled={mapLoading}
                className="w-full inline-flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {mapLoading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    <span>Refreshing...</span>
                  </>
                ) : (
                  <>
                    <Settings className="w-4 h-4" />
                    <span>Refresh Map</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Display Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2 mb-4">
          <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Display Settings</h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">(Visual only - no data refresh)</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="colorStyle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Color Style
            </label>
            <select
              id="colorStyle"
              name="colorStyle"
              value={displaySettings.colorStyle}
              onChange={handleDisplaySettingsChange}
              className="form-select dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="orange-red">Orange to Red (Low to High)</option>
              <option value="green-blue">Green to Blue (Low to High)</option>
              <option value="yellow-purple">Yellow to Purple (Low to High)</option>
              <option value="green-red">Green to Red (Low to High)</option>
              <option value="grayscale">Grayscale (Low to High)</option>
            </select>
          </div>

          <div>
            <label htmlFor="colorMetric" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Color By
            </label>
            <select
              id="colorMetric"
              name="colorMetric"
              value={displaySettings.colorMetric}
              onChange={handleDisplaySettingsChange}
              className="form-select dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="load_count">Total Loads</option>
              <option value="total_payment">Market Size</option>
              <option value="avg_payment">Average Payment</option>
              <option value="avg_rate">Average Rate</option>
            </select>
          </div>

          <div>
            <label htmlFor="colorScale" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Color Scale
            </label>
            <select
              id="colorScale"
              name="colorScale"
              value={displaySettings.colorScale}
              onChange={handleDisplaySettingsChange}
              className="form-select dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <option value="linear">Linear</option>
              <option value="quantile">Quantile (Equal Distribution)</option>
              <option value="logarithmic">Logarithmic</option>
              <option value="jenks">Natural Breaks</option>
              <option value="percentile">Percentile (95th Cap)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Map Container */}
      {loading ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-8">
          <div className="flex items-center justify-center space-x-3">
            <Loader className="w-6 h-6 text-primary-600 animate-spin" />
            <span className="text-gray-600 dark:text-gray-400">Loading freight data...</span>
          </div>
        </div>
      ) : error ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-red-200 dark:border-red-700 p-8">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Map className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Data Loading Error</h3>
            <p className="text-gray-600 dark:text-gray-400">{error}</p>
          </div>
        </div>
      ) : showMap ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="h-[800px]">
            <MapResults 
              results={filteredData} 
              isVisible={showMap}
              viewType={backendFilters.viewType}
              colorMetric={displaySettings.colorMetric}
              colorStyle={displaySettings.colorStyle}
              colorScale={displaySettings.colorScale}
              filters={{
                equipment_types: backendFilters.equipment !== 'all' ? getEquipmentTypes(backendFilters.equipment) : undefined,
                pickup_date_begin: getDateRangeFilter(backendFilters.dateRange)?.start,
                pickup_date_end: getDateRangeFilter(backendFilters.dateRange)?.end
              }}
            />
          </div>
          
          {/* Map Footer with Data Layer Control */}
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-t border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {(backendFilters.viewType === 'state' || backendFilters.viewType === 'county') ? 
                  `Showing aggregated ${mapFilters.dataType} data (${backendFilters.viewType} view) from backend` :
                  `Showing ${mapFilters.dataType} data for ${backendFilters.dateRange.replace(/\d+/, match => match + ' ')} (${filteredData.length} loads from ${dataSource})`
                }
              </div>
              <div className="flex items-center space-x-4">
                {/* Original Data Layer dropdown for non-Heat Map views */}
                {!(backendFilters.viewType === 'state' || backendFilters.viewType === 'county') && (
                  <div className="flex items-center space-x-2">
                    <label htmlFor="dataTypeFooter" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Data Layer:
                    </label>
                    <select
                      id="dataTypeFooter"
                      name="dataType"
                      value={mapFilters.dataType}
                      onChange={handleVisualChange}
                      className="form-select text-sm dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    >
                      <option value="loads">Load Distribution</option>
                      <option value="rates">Rate Heatmap</option>
                      <option value="lanes">Popular Lanes</option>
                      <option value="brokers">Broker Locations</option>
                    </select>
                  </div>
                )}
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Interactive Mapbox visualization
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-8">
          <div className="text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Map className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Map Ready to Load</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Click "Show Map" above to visualize {filteredData.length} freight loads on the interactive map
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Map will center on USA by default
            </p>
          </div>
        </div>
      )}

      {/* Map Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active Locations</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {mapStats.activeLocations.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
              <Truck className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active Lanes</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {mapStats.activeLanes.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Rate/Mile</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ${mapStats.avgRatePerMile.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MapView;